import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FormControl, MenuItem, Pagination, Select, Typography } from "@mui/material";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { allOrdersMealsPath, allOrdersPath, allOrdersPricePath, DESSERT_DAYS_PATH, mealsPath, SIDE_DAYS_PATH, snacksPath, studentsPath } from "../../../functions/src/admin/paths";
import { formatOrderDayLabel, formatOrderPlacedDateLabel, formatWalletHistorySchoolYearLabel, toDateFromServer } from "../../../functions/src/api/dates";
import type { BaseOrderLine, Meal, MealLine, Order, OrderPerson, SnackLine } from "../../../functions/src/types";
import { groupOrderHistoryDays, orderHistoryDayMetaFromValue, useOrderHistoryYearSetup, useUserOrdersForSchoolYear, type OrderHistoryDayMeta, type OrderHistoryGroup } from "../../api/orderHistory";
import { auth, currentCatering, DataSnapshot } from "../../api/firebase";
import { useDatabase, useDatabaseVal, useList } from "../../api/firebaseHooks";
import { getLangValue, langContext, type Language } from "../../api/Lang";
import { getMealData, getMealName, useDailyMealsList, useMenuData } from "../../api/mealUtils";
import { getPortionSizeLabel, usePortionSizeLabels } from "../../api/portionUtils";
import { formatPriceEuro } from "../../api/priceFormat";
import { useStrings } from "../../common-strings";
import { useOrderHistoryStrings, type OrderHistoryStrings } from "./order-history-strings";

const PAGE_SIZE = 20;

function isPlacedOrderDay(
    price: number | null | undefined,
    mealsList: DataSnapshot<MealLine>[] | undefined,
    snacksList: DataSnapshot<SnackLine>[] | undefined
): boolean {
    if (typeof price !== "number") return false;
    const hasMeals = !!mealsList?.some((m) => (m.val()?.quantity ?? 0) > 0);
    const hasSnacks = !!snacksList?.some((s) => (s.val()?.quantity ?? 0) > 0);
    return price > 0 || hasMeals || hasSnacks;
}

function getOrderLineTotal(line: BaseOrderLine): number {
    const quantity = line.quantity ?? 0;
    if (quantity <= 0) return 0;
    if (line.unitPrice != null && !Number.isNaN(line.unitPrice)) {
        return Math.round(line.unitPrice * quantity * 100) / 100;
    }
    return 0;
}

type OrderHistoryLine = {
    key: string;
    recipientKey: string;
    recipient: string;
    itemLabel: string;
    extras?: string;
    quantity: number;
    lineTotal: number;
};

type OrderHistoryRecipientGroup = {
    recipientKey: string;
    recipient: string;
    lines: OrderHistoryLine[];
};

function groupOrderHistoryLinesByRecipient(lines: OrderHistoryLine[]): OrderHistoryRecipientGroup[] {
    const groups: OrderHistoryRecipientGroup[] = [];
    const indexByKey = new Map<string, number>();
    for (const line of lines) {
        const mapKey = line.recipientKey || "__none__";
        const existingIndex = indexByKey.get(mapKey);
        if (existingIndex === undefined) {
            indexByKey.set(mapKey, groups.length);
            groups.push({ recipientKey: line.recipientKey, recipient: line.recipient, lines: [line] });
        } else {
            groups[existingIndex].lines.push(line);
        }
    }
    return groups;
}

function getOrderHistoryMealExtras(
    mealLine: MealLine,
    foodItem: Meal | null,
    sideDish: Meal | undefined,
    dessert: Meal | undefined,
    sideDay: string,
    dessertDay: string,
    lang: Language,
): string | undefined {
    const mealSide = getLangValue(foodItem?.sideDish, lang);
    const sideMenu = sideDish ? getLangValue(sideDish.name, lang) : "";
    const sideName = mealSide || sideMenu || sideDay;
    const dessertMenu = dessert ? getLangValue(dessert.name, lang) : "";
    const dessertName = dessertMenu || dessertDay;
    const parts: string[] = [];
    if ((mealLine.quantitySide ?? 0) > 0 && sideName) parts.push(sideName);
    if ((mealLine.quantityDessert ?? 0) > 0 && dessertName) parts.push(dessertName);
    return parts.length ? parts.join(", ") : undefined;
}

export default function OrderHistory() {
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
    const [lang] = useContext(langContext);
    const strings = useOrderHistoryStrings();
    const commonStrings = useStrings();
    const uid = auth.currentUser?.uid;
    const { ready, schoolYearStartYear, setSchoolYearStartYear, schoolYearOptions } = useOrderHistoryYearSetup();
    const userOrdersSnapshot = useUserOrdersForSchoolYear(schoolYearStartYear);
    const userOrdersList = useList(userOrdersSnapshot);
    const [page, setPage] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    const studentsSnapshot = useDatabase(uid ? studentsPath(uid) : null);
    const studentsList = useList(studentsSnapshot);

    const recipientNames = useMemo(() => {
        const map: Record<string, string> = {};
        for (const snap of studentsList ?? []) {
            const person = snap.val() as OrderPerson | undefined;
            if (!person || !snap.key) continue;
            const name = `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim();
            map[snap.key] = name || snap.key;
        }
        return map;
    }, [studentsList]);

    const orderGroups = useMemo(() => {
        if (!userOrdersList?.length) return [];
        const days = userOrdersList
            .map((snap) => orderHistoryDayMetaFromValue(snap.key!, snap.val() as Order | null))
            .filter((day): day is OrderHistoryDayMeta => day != null);
        return groupOrderHistoryDays(days);
    }, [userOrdersList]);

    const totalPages = Math.max(1, Math.ceil(orderGroups.length / PAGE_SIZE));
    const pageGroups = orderGroups.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
    const rangeFrom = orderGroups.length === 0 ? 0 : page * PAGE_SIZE + 1;
    const rangeTo = Math.min((page + 1) * PAGE_SIZE, orderGroups.length);

    useEffect(() => {
        setPage(0);
        setExpandedGroups(new Set());
    }, [schoolYearStartYear, userOrdersSnapshot]);

    useEffect(() => {
        if (page >= totalPages) {
            setPage(Math.max(0, totalPages - 1));
        }
    }, [page, totalPages]);

    useEffect(() => {
        listRef.current?.scrollTo(0, 0);
    }, [page, schoolYearStartYear]);

    const toggleGroup = useCallback((groupId: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    }, []);

    if (!ready) {
        return (
            <div className="order-history-inline wallet-history-dialog-content">
                <div className="wallet-history-empty">{commonStrings.loading}</div>
            </div>
        );
    }

    return (
        <div className="order-history-inline wallet-history-dialog-content">
            <FormControl size="small" className="wallet-history-year-select" sx={{ minWidth: 140, mb: 2 }}>
                <Select
                    value={schoolYearStartYear!}
                    onChange={(e) => setSchoolYearStartYear(Number(e.target.value))}
                    displayEmpty
                    aria-label={strings.schoolYearLabel}
                >
                    {schoolYearOptions.map((startYear) => (
                        <MenuItem key={startYear} value={startYear}>
                            {formatWalletHistorySchoolYearLabel(startYear)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {!userOrdersSnapshot && (
                <div className="wallet-history-empty">{commonStrings.loading}</div>
            )}
            {userOrdersSnapshot && orderGroups.length === 0 && (
                <div className="wallet-history-empty">{strings.emptyForSchoolYear}</div>
            )}
            {pageGroups.length > 0 && (
                <div ref={listRef} className="wallet-history-list order-history-list">
                    {pageGroups.map((group) => (
                        <OrderHistoryGroupRow
                            key={group.id}
                            group={group}
                            expanded={expandedGroups.has(group.id)}
                            onToggle={() => toggleGroup(group.id)}
                            recipientNames={recipientNames}
                            lang={lang}
                            strings={strings}
                        />
                    ))}
                </div>
            )}
            {orderGroups.length > PAGE_SIZE && (
                <div className="wallet-history-pagination">
                    <Typography variant="caption" component="span" className="wallet-history-more">
                        {strings.pageSummary(rangeFrom, rangeTo, orderGroups.length)}
                    </Typography>
                    <Pagination
                        count={totalPages}
                        page={page + 1}
                        onChange={(_, value) => setPage(value - 1)}
                        size="small"
                        color="primary"
                        sx={{
                            "& .MuiPaginationItem-root.Mui-selected": {
                                backgroundColor: "var(--lb_green)",
                            },
                        }}
                    />
                </div>
            )}
        </div>
    );
}

type OrderHistoryGroupRowProps = {
    group: OrderHistoryGroup;
    expanded: boolean;
    onToggle: () => void;
    recipientNames: Record<string, string>;
    lang: Language;
    strings: OrderHistoryStrings;
};

function OrderHistoryGroupRow({
    group,
    expanded,
    onToggle,
    recipientNames,
    lang,
    strings,
}: OrderHistoryGroupRowProps) {
    // Legacy rows with no placed/changed time — fall back to the delivery day as the title.
    if (group.dates.length === 1 && group.placedAt === 0) {
        return (
            <OrderHistoryDateRow
                date={group.dates[0]}
                expanded={expanded}
                onToggle={onToggle}
                recipientNames={recipientNames}
                lang={lang}
                strings={strings}
            />
        );
    }

    return (
        <div className={`order-history-date-row${expanded ? " expanded" : ""}`}>
            <button type="button" className="order-history-date-toggle" onClick={onToggle}>
                <span className="order-history-date-label">
                    {strings.orderPlaced(formatOrderPlacedDateLabel(group.placedAt, lang))}
                </span>
                <span className="order-history-date-end">
                    <span className="order-history-total">{formatPriceEuro(group.totalPrice, lang)}</span>
                    {expanded
                        ? <ExpandLessIcon className="order-history-chevron" aria-hidden />
                        : <ExpandMoreIcon className="order-history-chevron" aria-hidden />
                    }
                </span>
            </button>
            {expanded && (
                <div className="order-history-checkout-days">
                    {group.dates.map((date) => (
                        <OrderHistoryGroupDay
                            key={date}
                            date={date}
                            recipientNames={recipientNames}
                            lang={lang}
                            strings={strings}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

type OrderHistoryDateRowProps = {
    date: string;
    expanded: boolean;
    onToggle: () => void;
    recipientNames: Record<string, string>;
    lang: Language;
    strings: OrderHistoryStrings;
};

function OrderHistoryDateRow({
    date,
    expanded,
    onToggle,
    recipientNames,
    lang,
    strings,
}: OrderHistoryDateRowProps) {
    const uid = auth.currentUser?.uid;
    const [price] = useDatabaseVal<number>(uid ? allOrdersPricePath(uid, date) : null);
    const mealsSnapshot = useDatabase(uid ? allOrdersMealsPath(uid, date) : null);
    const mealsList = useList(mealsSnapshot);
    const snacksSnapshot = useDatabase(uid ? `${allOrdersPath(uid, date)}/snacks` : null);
    const snacksList = useList(snacksSnapshot);

    if (!isPlacedOrderDay(price, mealsList, snacksList)) return null;

    return (
        <div className={`order-history-date-row${expanded ? " expanded" : ""}`}>
            <button type="button" className="order-history-date-toggle" onClick={onToggle}>
                <span className="order-history-date-label">{formatOrderDayLabel(date, lang)}</span>
                <span className="order-history-date-end">
                    <span className="order-history-total">{formatPriceEuro(price ?? 0, lang)}</span>
                    {expanded
                        ? <ExpandLessIcon className="order-history-chevron" aria-hidden />
                        : <ExpandMoreIcon className="order-history-chevron" aria-hidden />
                    }
                </span>
            </button>
            {expanded && (
                <OrderHistoryDayLines
                    date={date}
                    mealsList={mealsList}
                    snacksList={snacksList}
                    recipientNames={recipientNames}
                    lang={lang}
                    strings={strings}
                />
            )}
        </div>
    );
}

type OrderHistoryGroupDayProps = {
    date: string;
    recipientNames: Record<string, string>;
    lang: Language;
    strings: OrderHistoryStrings;
};

function OrderHistoryGroupDay({
    date,
    recipientNames,
    lang,
    strings,
}: OrderHistoryGroupDayProps) {
    const uid = auth.currentUser?.uid;
    const [price] = useDatabaseVal<number>(uid ? allOrdersPricePath(uid, date) : null);
    const mealsSnapshot = useDatabase(uid ? allOrdersMealsPath(uid, date) : null);
    const mealsList = useList(mealsSnapshot);
    const snacksSnapshot = useDatabase(uid ? `${allOrdersPath(uid, date)}/snacks` : null);
    const snacksList = useList(snacksSnapshot);

    if (!isPlacedOrderDay(price, mealsList, snacksList)) return null;

    return (
        <div className="order-history-group-day">
            <div className="order-history-group-day-header">
                <span className="order-history-group-day-label">{formatOrderDayLabel(date, lang)}</span>
                <span className="order-history-group-day-total">{formatPriceEuro(price ?? 0, lang)}</span>
            </div>
            <OrderHistoryDayLines
                date={date}
                mealsList={mealsList}
                snacksList={snacksList}
                recipientNames={recipientNames}
                lang={lang}
                strings={strings}
            />
        </div>
    );
}

type OrderHistoryDayLinesProps = {
    date: string;
    mealsList: DataSnapshot<MealLine>[] | undefined;
    snacksList: DataSnapshot<SnackLine>[] | undefined;
    recipientNames: Record<string, string>;
    lang: Language;
    strings: OrderHistoryStrings;
};

function OrderHistoryDayLines({
    date,
    mealsList,
    snacksList,
    recipientNames,
    lang,
    strings,
}: OrderHistoryDayLinesProps) {
    const dailyMealsList = useDailyMealsList();
    const { menuData, menuItems } = useMenuData(date);
    const sFoods = useDatabase(mealsPath(currentCatering));
    const sSnacks = useDatabase(snacksPath(currentCatering));
    const dayNum = toDateFromServer(date).getDay();
    const [sideDay] = useDatabaseVal<string>(`${SIDE_DAYS_PATH}/${dayNum}/${lang}`);
    const [dessertDay] = useDatabaseVal<string>(`${DESSERT_DAYS_PATH}/${dayNum}/${lang}`);
    const sideDish = menuItems.find((item) => item.key === "sideMenu")?.val();
    const dessert = menuItems.find((item) => item.key === "dessertMenu")?.val();
    const portionLabels = usePortionSizeLabels();

    const lines = useMemo(() => {
        const items: OrderHistoryLine[] = [];

        for (const mealSnap of mealsList ?? []) {
            const meal = mealSnap.val();
            if (!meal || (meal.quantity ?? 0) <= 0) continue;
            const foodItem = getMealData(meal.key, sFoods, dailyMealsList, menuData);
            const mealName =
                getMealName(meal.key, sFoods, dailyMealsList, lang, menuData) ||
                (meal.isPackage ? strings.packageMeal : meal.key);
            const itemLabel = meal.portionSize
                ? `${mealName} (${getPortionSizeLabel(meal.portionSize, portionLabels)})`
                : mealName;
            const recipientKey = meal.recipient ?? "";
            const recipient = recipientNames[meal.recipient] || meal.recipient;
            items.push({
                key: `meal-${mealSnap.key}`,
                recipientKey,
                recipient,
                itemLabel,
                extras: getOrderHistoryMealExtras(
                    meal,
                    foodItem,
                    sideDish,
                    dessert,
                    sideDay ?? "",
                    dessertDay ?? "",
                    lang,
                ),
                quantity: meal.quantity,
                lineTotal: getOrderLineTotal(meal),
            });
        }

        for (const snackSnap of snacksList ?? []) {
            const snack = snackSnap.val();
            if (!snack || (snack.quantity ?? 0) <= 0) continue;
            const snackName =
                getLangValue(snack.snackKey ? sSnacks?.child(snack.snackKey)?.val()?.name : undefined, lang) ||
                strings.snack;
            const recipientKey = snack.recipient ?? "";
            const recipient = recipientNames[snack.recipient] || snack.recipient;
            items.push({
                key: `snack-${snackSnap.key}`,
                recipientKey,
                recipient,
                itemLabel: snackName,
                quantity: snack.quantity,
                lineTotal: getOrderLineTotal(snack),
            });
        }

        return items;
    }, [dailyMealsList, dessert, dessertDay, lang, mealsList, menuData, portionLabels, recipientNames, sFoods, sSnacks, sideDay, sideDish, snacksList, strings]);

    const recipientGroups = useMemo(() => groupOrderHistoryLinesByRecipient(lines), [lines]);

    if (!recipientGroups.length) return null;

    return (
        <div className="order-history-day-lines">
            {recipientGroups.map((group, groupIndex) => (
                <div
                    key={group.recipientKey || `group-${groupIndex}`}
                    className={`order-history-recipient-group${groupIndex > 0 ? " order-history-recipient-group--spaced" : ""}`}
                >
                    {!!group.recipient && (
                        <span className="order-history-line-person">{group.recipient}</span>
                    )}
                    {group.lines.map((line) => (
                        <div key={line.key} className="order-history-line">
                            <div className="order-history-line-body">
                                <div className="order-history-line-row">
                                    <span className="order-history-line-meal">{line.itemLabel}</span>
                                    <span className="order-history-line-end">
                                        {line.quantity > 1 && (
                                            <span className="order-history-line-qty">{strings.qty(line.quantity)}</span>
                                        )}
                                        <span className="order-history-line-price">
                                            {formatPriceEuro(line.lineTotal, lang)}
                                        </span>
                                    </span>
                                </div>
                                {line.extras && (
                                    <span className="order-history-line-extras">{line.extras}</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
